import type { ReactNode } from 'react';
import { Link, type To } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Banner } from '@/components/ui/Banner';
import { Button, ButtonLink } from '@/components/ui/Button';
import { Avatar } from '@/components/ui/Avatar';
import { CardHeader, ListRow } from '@/components/ui/Card';
import { StatusChip, type StatusTone } from '@/components/ui/Chip';
import { EmptyState } from '@/components/ui/EmptyState';
import { SkeletonText } from '@/components/ui/Skeleton';
import { TYPE, TYPE_TABULAR, UI_MUTED } from '@/lib/typography';
import { cn } from '@/utils/cn';
import {
  AlertCircle,
  Calendar,
  Check,
  ChevronRight,
  FilePenLine,
  FileText,
  Mail,
  PenLine,
  Plus,
  Smartphone,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { useAppointmentsInRange } from '@/hooks/useAppointments';
import { countedPatientAppointments } from '@/utils/calendarTimeBlock';
import { usePatientsList } from '@/hooks/usePatients';
import { useInvoicesList } from '@/hooks/useInvoices';
import { useSmsClinic } from '@/hooks/useSms';
import { useAcknowledgeAlert, useClinicalAlerts } from '@/hooks/useClinicalAlerts';
import { useQuestionnaireNameMap } from '@/hooks/useQuestionnaireNameMap';
import { resolveQuestionnaireDisplayName } from '@/lib/questionnaireDisplayName';
import {
  useAssignedPatientEmailInbox,
  useMarkPatientEmailThreadRead,
} from '@/hooks/usePatientEmailThreads';
import { usePatientMessageInbox } from '@/hooks/usePatientMessages';
import {
  dashboardKommunikationEmailPath,
  dashboardKommunikationUnreadCount,
  mergeDashboardKommunikationItems,
} from '@/lib/dashboardKommunikation';
import { useStaffTasks } from '@/hooks/useStaffTasks';
import { compareStaffTasks, staffTaskIsOverdue, staffTaskIsOpen } from '@/lib/staffTasks';
import { useAuth } from '@/lib/auth';
import { useCalendarAppointmentMeta } from '@/hooks/useCalendarAppointmentMeta';
import {
  calendarJournalIsCompleted,
  type CalendarJournalMeta,
} from '@/utils/appointmentCalendarMeta';
import {
  appointmentStatusLabelKey,
  resolveAppointmentStatus,
  type AppointmentLifecycleStatus,
} from '@/utils/appointmentStatus';
import type { DashboardWidgetId } from '@/pages/dashboard/widgetCatalog';
import { patientDisplayName, patientMessagesPath } from '@/utils/patientMessagesPath';
import {
  conversationLatestSender,
  defaultMessageSenderRoleLabel,
  messageSenderRoleKey,
} from '@/utils/messageSender';
import { destinationHref, invoiceDestination } from '@/utils/contextualDestinations';
import {
  INVOICE_REPORT_FILTERS,
  matchingInvoiceRows,
  sumMatchingInvoiceTotals,
  toInvoiceListQueryFilters,
} from '@/pages/invoices/invoiceFilters';
import {
  appointmentIsCurrent,
  formatDashboardMessageTime,
  formatDashboardMoney,
  formatDashboardTime,
  formatDashboardTimeRange,
} from '@/pages/dashboard/dashboardFormat';
import {
  dashboardAppointmentCalendarHref,
  dashboardAppointmentClinicId,
  dashboardAppointmentFilters,
} from '@/pages/dashboard/dashboardAppointments';

function appointmentChip(
  lifecycle: AppointmentLifecycleStatus,
): { tone: StatusTone; labelKey: string } {
  switch (lifecycle) {
    case 'upcoming':
    case 'checked_in':
      return { tone: 'upcoming', labelKey: appointmentStatusLabelKey(lifecycle) };
    case 'under_treatment':
      return { tone: 'draft', labelKey: appointmentStatusLabelKey(lifecycle) };
    case 'treatment_finished':
    case 'completed':
      return { tone: 'completed', labelKey: 'dashboard.status_completed' };
    case 'cancelled':
      return { tone: 'cancelled', labelKey: appointmentStatusLabelKey(lifecycle) };
    case 'no_show':
      return { tone: 'no_show', labelKey: appointmentStatusLabelKey(lifecycle) };
    default: {
      const _exhaustive: never = lifecycle;
      return _exhaustive;
    }
  }
}

function journalRail(
  journal: CalendarJournalMeta | undefined,
  cancelled: boolean,
): 'draft' | 'finalized' | 'cancelled' {
  if (cancelled) return 'cancelled';
  if (calendarJournalIsCompleted(journal)) return 'finalized';
  return 'draft';
}

export function DashboardWidgetBody({
  id,
  clinicId,
}: {
  id: DashboardWidgetId;
  clinicId: string | null;
}) {
  switch (id) {
    case 'quick_actions':
      return <QuickActionsWidget clinicId={clinicId} />;
    case 'today_appointments':
      return <TodayAppointmentsKpi clinicId={clinicId} />;
    case 'active_patients':
      return <ActivePatientsKpi clinicId={clinicId} />;
    case 'outstanding_invoices':
      return <OutstandingInvoicesKpi clinicId={clinicId} />;
    case 'sms_balance':
      return <SmsBalanceKpi clinicId={clinicId} />;
    case 'todays_schedule':
      return <TodaysScheduleWidget clinicId={clinicId} />;
    case 'month_summary':
      return <MonthSummaryWidget clinicId={clinicId} />;
    case 'operational_alerts':
      return <OperationalAlertsWidget clinicId={clinicId} />;
    case 'clinical_alerts':
      return <ClinicalAlertsWidget clinicId={clinicId} />;
    case 'patient_messages':
      return <PatientMessagesWidget clinicId={clinicId} />;
    case 'my_tasks':
      return <MyTasksWidget clinicId={clinicId} />;
    default: {
      const _exhaustive: never = id;
      return _exhaustive;
    }
  }
}

function WidgetNavLink({ to, children }: { to: To; children: ReactNode }) {
  return (
    <Link
      to={to}
      className={cn(
        TYPE.control,
        'inline-flex shrink-0 items-center gap-s1 font-semibold text-ds-teal hover:text-ds-teal-700',
      )}
    >
      {children}
      <ChevronRight className="h-4 w-4" aria-hidden />
    </Link>
  );
}

function WidgetQueryState({
  loading,
  error,
  onRetry,
  children,
}: {
  loading: boolean;
  error: boolean;
  onRetry?: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  if (loading) return <SkeletonText lines={3} className="mt-s4" />;
  if (error) {
    return (
      <Banner
        tone="error"
        className="mt-s4"
        action={
          onRetry ? (
            <Button variant="secondary" size="sm" onClick={onRetry}>
              {t('dashboard.retry', 'Prøv igen')}
            </Button>
          ) : undefined
        }
      >
        {t('dashboard.load_error', 'Kunne ikke hentes. Tjek forbindelsen, og prøv igen.')}
      </Banner>
    );
  }
  return <>{children}</>;
}

function QuickActionsWidget({ clinicId }: { clinicId: string | null }) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canPatient = Boolean(clinicId && hasPermission(clinicId, 'patients', 'create'));
  const canAppointment = Boolean(clinicId && hasPermission(clinicId, 'appointments', 'create'));
  const canInvoice = Boolean(clinicId && hasPermission(clinicId, 'invoices', 'create'));

  if (!canPatient && !canAppointment && !canInvoice) {
    return <p className={UI_MUTED}>{t('dashboard.quick_actions_empty', 'Ingen hurtige handlinger for din rolle')}</p>;
  }

  return (
    <div className="flex h-full flex-wrap items-center gap-s2">
      {canPatient && (
        <ButtonLink to="/patients/new" variant="secondary">
          <Plus /> {t('dashboard.new_patient', 'Ny patient')}
        </ButtonLink>
      )}
      {canAppointment && (
        <ButtonLink to="/appointments" variant="secondary">
          <Plus /> {t('dashboard.new_appointment', 'Ny aftale')}
        </ButtonLink>
      )}
      {canInvoice && (
        <ButtonLink to="/invoices/new" variant="secondary">
          <Plus /> {t('dashboard.new_invoice', 'Ny faktura')}
        </ButtonLink>
      )}
    </div>
  );
}

function TodayAppointmentsKpi({ clinicId }: { clinicId: string | null }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const today = new Date();
  const practitionerId = user?.id ?? null;
  const todayApts = useAppointmentsInRange(
    dashboardAppointmentClinicId(clinicId, practitionerId),
    dashboardAppointmentFilters(today, practitionerId, 'today'),
  );
  const patientToday = countedPatientAppointments(todayApts.data ?? []);
  const upcomingToday = patientToday.filter(
    (a) => new Date(a.starts_at).getTime() >= today.getTime(),
  );
  const nextAppointment = upcomingToday[0] ?? null;
  const hint =
    nextAppointment
      ? t('dashboard.next_at', 'Næste kl. {{time}}: {{name}}', {
          time: formatDashboardTime(nextAppointment.starts_at, i18n.language),
          name: nextAppointment.patient_name,
        })
      : patientToday.length === 0
        ? t('dashboard.no_appointments_today', 'Ingen aftaler i dag')
        : undefined;

  return (
    <KpiCard
      icon={Calendar}
      label={t('dashboard.today_appointments', 'Aftaler i dag')}
      value={String(patientToday.length)}
      hint={hint}
      loading={!practitionerId || todayApts.isLoading}
      error={todayApts.isError}
      onRetry={() => void todayApts.refetch()}
      to={dashboardAppointmentCalendarHref(today, practitionerId, 'today')}
      cta={t('dashboard.view_calendar', 'Vis kalender')}
    />
  );
}

function ActivePatientsKpi({ clinicId }: { clinicId: string | null }) {
  const { t } = useTranslation();
  const patientsQ = usePatientsList(clinicId, { status: 'active' });
  return (
    <KpiCard
      icon={Users}
      label={t('dashboard.active_patients', 'Aktive patienter')}
      value={String(patientsQ.data?.length ?? 0)}
      hint={t('dashboard.this_clinic', 'På denne klinik')}
      loading={patientsQ.isLoading}
      error={patientsQ.isError}
      onRetry={() => void patientsQ.refetch()}
      to="/patients"
      cta={t('dashboard.view_patients', 'Vis patienter')}
    />
  );
}

function OutstandingInvoicesKpi({ clinicId }: { clinicId: string | null }) {
  const { t, i18n } = useTranslation();
  const now = new Date();
  const outstandingFilters = INVOICE_REPORT_FILTERS.outstanding;
  const invoicesQ = useInvoicesList(clinicId, toInvoiceListQueryFilters(outstandingFilters, now));
  const invoices = invoicesQ.data ?? [];
  const issuedInvoices = matchingInvoiceRows(invoices, outstandingFilters, now);
  const overdueInvoices = matchingInvoiceRows(invoices, INVOICE_REPORT_FILTERS.overdue, now);
  const partialInvoices = matchingInvoiceRows(invoices, INVOICE_REPORT_FILTERS.partial, now);
  const outstandingTotal = issuedInvoices.reduce(
    (sum, i) => sum + (i.total_cents - i.total_paid_cents),
    0,
  );
  const breakdown =
    overdueInvoices.length > 0 || partialInvoices.length > 0
      ? t('dashboard.x_overdue_y_partial', '{{overdue}} forfaldne · {{partial}} delvist betalt', {
          overdue: overdueInvoices.length,
          partial: partialInvoices.length,
        })
      : null;

  return (
    <KpiCard
      icon={FileText}
      label={t('dashboard.outstanding_invoices', 'Udestående fakturaer')}
      value={String(issuedInvoices.length)}
      hint={[breakdown, formatDashboardMoney(outstandingTotal, i18n.language)]
        .filter(Boolean)
        .join(' · ')}
      loading={invoicesQ.isLoading}
      error={invoicesQ.isError}
      onRetry={() => void invoicesQ.refetch()}
      accent={overdueInvoices.length > 0 ? 'warning' : undefined}
      to={destinationHref(invoiceDestination(outstandingFilters))}
      cta={t('dashboard.view_invoices', 'Vis fakturaer')}
    />
  );
}

function SmsBalanceKpi({ clinicId }: { clinicId: string | null }) {
  const { t, i18n } = useTranslation();
  const smsQ = useSmsClinic(clinicId);
  const smsBalance = smsQ.data?.credits.balance ?? 0;
  const smsLowBalance =
    smsQ.data &&
    smsQ.data.credits.lowBalanceThreshold > 0 &&
    smsQ.data.credits.balance <= smsQ.data.credits.lowBalanceThreshold;

  return (
    <KpiCard
      icon={Smartphone}
      label={t('dashboard.sms_balance', 'SMS-saldo')}
      value={smsQ.data ? formatDashboardMoney(smsBalance, i18n.language) : '—'}
      hint={
        smsLowBalance
          ? t('dashboard.low_balance', 'Lav saldo — køb en pakke')
          : t('dashboard.sms_ready', 'Klar til afsendelse')
      }
      loading={smsQ.isLoading}
      error={smsQ.isError}
      onRetry={() => void smsQ.refetch()}
      accent={smsLowBalance ? 'warning' : undefined}
      to="/settings/sms"
      cta={t('dashboard.view_sms', 'Vis SMS')}
    />
  );
}

function TodaysScheduleWidget({ clinicId }: { clinicId: string | null }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const today = new Date();
  const practitionerId = user?.id ?? null;
  const todayApts = useAppointmentsInRange(
    dashboardAppointmentClinicId(clinicId, practitionerId),
    dashboardAppointmentFilters(today, practitionerId, 'today'),
  );
  const appointments = countedPatientAppointments(todayApts.data ?? []);
  const calendarMeta = useCalendarAppointmentMeta(clinicId, appointments);
  const calendarTo = dashboardAppointmentCalendarHref(today, practitionerId, 'today');

  return (
    <section className="flex h-full min-h-0 flex-col" aria-labelledby="dashboard-todays-schedule">
      <CardHeader
        className="flex-wrap"
        title={
          <span id="dashboard-todays-schedule" className="inline-flex items-center gap-s3">
            {t('dashboard.todays_schedule', 'Dagens program')}
            {appointments.length > 0 ? (
              <span
                className={cn(
                  TYPE.meta,
                  TYPE_TABULAR,
                  'inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-ds-neutral-tint px-s2 text-ds-muted',
                )}
              >
                {appointments.length}
              </span>
            ) : null}
          </span>
        }
        action={
          <WidgetNavLink to={calendarTo}>{t('dashboard.view_calendar', 'Vis kalender')}</WidgetNavLink>
        }
      />

      <WidgetQueryState
        loading={!practitionerId || todayApts.isLoading}
        error={todayApts.isError}
        onRetry={() => void todayApts.refetch()}
      >
        {appointments.length === 0 ? (
          <EmptyState
            className="py-s5"
            icon={<Calendar className="h-7 w-7" />}
            title={t('dashboard.empty_schedule_title', 'Ingen aftaler i dag')}
            description={t(
              'dashboard.empty_schedule_body',
              'Når dagens aftaler ligger her, kan du åbne kalenderen og starte journalen.',
            )}
            action={
              <ButtonLink to={calendarTo} variant="secondary">
                <Plus /> {t('dashboard.new_appointment', 'Ny aftale')}
              </ButtonLink>
            }
          />
        ) : (
          <>
            <div className="mt-s4 min-h-0 flex-1 overflow-y-auto">
              {appointments.map((a) => {
                const lifecycle = resolveAppointmentStatus({
                  status: a.status,
                  visitStatus: a.visit_status,
                  startsAt: a.starts_at,
                  endsAt: a.ends_at,
                });
                const cancelled = lifecycle === 'cancelled';
                const journal = calendarMeta.get(a.id)?.journal;
                const chip = appointmentChip(lifecycle);
                const current = appointmentIsCurrent(a.starts_at, a.ends_at, today, cancelled);
                return (
                  <ListRow
                    key={a.id}
                    rail={journalRail(journal, cancelled)}
                    className={cn(
                      'items-start px-0 last:border-b-0 nav:items-center',
                      current && '-mx-s4 bg-ds-coral-tint px-s4 nav:-mx-s5 nav:px-s5',
                    )}
                  >
                    <p
                      className={cn(
                        TYPE.body,
                        TYPE_TABULAR,
                        'w-[5.5rem] shrink-0 font-medium text-ds-text',
                      )}
                    >
                      {formatDashboardTimeRange(a.starts_at, a.ends_at, i18n.language)}
                    </p>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-s2">
                        {a.patient_id ? (
                          <Link
                            to={`/patients/${a.patient_id}`}
                            className={cn(TYPE.section, 'truncate text-ds-text hover:text-ds-teal')}
                          >
                            {a.patient_name}
                          </Link>
                        ) : (
                          <p className={cn(TYPE.section, 'truncate text-ds-text')}>{a.patient_name}</p>
                        )}
                        {current ? (
                          <StatusChip tone="coral">{t('dashboard.now', 'Nu')}</StatusChip>
                        ) : null}
                      </div>
                      <p className={cn(TYPE.small, 'mt-s1 truncate text-ds-muted')}>
                        {a.service_name ?? t('dashboard.no_service', 'Ingen ydelse')}
                      </p>
                      <div className="mt-s2 flex flex-wrap gap-s2 nav:hidden">
                        <StatusChip tone={chip.tone}>{t(chip.labelKey)}</StatusChip>
                        <JournalChip journal={journal} cancelled={cancelled} />
                      </div>
                    </div>
                    <div className="hidden shrink-0 flex-wrap justify-end gap-s2 nav:flex">
                      <StatusChip tone={chip.tone}>{t(chip.labelKey)}</StatusChip>
                      <JournalChip journal={journal} cancelled={cancelled} />
                    </div>
                  </ListRow>
                );
              })}
            </div>
            <div className={cn(TYPE.small, 'mt-s4 flex flex-wrap gap-s4 text-ds-muted')}>
              <span className="inline-flex items-center gap-s2">
                <i className="inline-block h-3 w-1 rounded-full bg-ds-teal" aria-hidden />
                {t('dashboard.journal_finalized', 'Journal færdiggjort')}
              </span>
              <span className="inline-flex items-center gap-s2">
                <i className="inline-block h-3 w-1 rounded-full bg-ds-teal-line" aria-hidden />
                {t('dashboard.journal_pending', 'Journal mangler')}
              </span>
            </div>
          </>
        )}
      </WidgetQueryState>
    </section>
  );
}

function JournalChip({
  journal,
  cancelled,
}: {
  journal: CalendarJournalMeta | undefined;
  cancelled: boolean;
}) {
  const { t } = useTranslation();
  if (cancelled) return null;
  if (calendarJournalIsCompleted(journal)) {
    return (
      <StatusChip tone="finalized">
        <Check className="h-3 w-3" aria-hidden />
        {t('dashboard.journal_finalized', 'Journal færdiggjort')}
      </StatusChip>
    );
  }
  if (journal?.attention === 'draft') {
    return (
      <StatusChip tone="draft">
        <PenLine className="h-3 w-3" aria-hidden />
        {t('dashboard.journal_draft', 'Journal: kladde')}
      </StatusChip>
    );
  }
  if (journal?.attention === 'needed') {
    return (
      <StatusChip tone="neutral">
        <FilePenLine className="h-3 w-3" aria-hidden />
        {t('dashboard.journal_pending', 'Journal mangler')}
      </StatusChip>
    );
  }
  return null;
}

function MonthSummaryWidget({ clinicId }: { clinicId: string | null }) {
  const { t, i18n } = useTranslation();
  const { user, hasPermission } = useAuth();
  const today = new Date();
  const practitionerId = user?.id ?? null;
  const canInvoices = Boolean(clinicId && hasPermission(clinicId, 'invoices', 'view'));
  const canAppointments = Boolean(clinicId && hasPermission(clinicId, 'appointments', 'view'));
  const paidThisMonthFilters = INVOICE_REPORT_FILTERS.paidThisMonth;

  const invoicesQ = useInvoicesList(
    canInvoices ? clinicId : null,
    toInvoiceListQueryFilters(paidThisMonthFilters, today),
  );
  const weekApts = useAppointmentsInRange(
    canAppointments ? dashboardAppointmentClinicId(clinicId, practitionerId) : null,
    dashboardAppointmentFilters(today, practitionerId, 'week'),
  );

  const paidThisMonth = sumMatchingInvoiceTotals(invoicesQ.data ?? [], paidThisMonthFilters, today);
  const detailsTo = canInvoices
    ? destinationHref(invoiceDestination(paidThisMonthFilters))
    : dashboardAppointmentCalendarHref(today, practitionerId, 'week');
  const loading =
    (canInvoices && invoicesQ.isLoading) ||
    (canAppointments && (!practitionerId || weekApts.isLoading));
  const error = (canInvoices && invoicesQ.isError) || (canAppointments && weekApts.isError);

  return (
    <section className="flex h-full min-h-0 flex-col" aria-labelledby="dashboard-month-summary">
      <CardHeader
        className="flex-wrap"
        title={<span id="dashboard-month-summary">{t('dashboard.month_summary', 'Denne måned')}</span>}
        action={<WidgetNavLink to={detailsTo}>{t('dashboard.view_details', 'Vis detaljer')}</WidgetNavLink>}
      />
      <WidgetQueryState
        loading={loading}
        error={error}
        onRetry={() => {
          if (canInvoices) void invoicesQ.refetch();
          if (canAppointments) void weekApts.refetch();
        }}
      >
        {!canInvoices && !canAppointments ? (
          <p className={cn(UI_MUTED, 'mt-s3')}>{t('dashboard.empty_month', 'Ingen nøgletal for denne måned.')}</p>
        ) : (
          <dl className="mt-s3 grid grid-cols-2 gap-s4">
            {canInvoices && (
              <div>
                <dt className={cn(TYPE.small, 'text-ds-muted')}>{t('dashboard.paid', 'Betalt')}</dt>
                <dd className={cn(TYPE.heading, TYPE_TABULAR, 'mt-s1 text-ds-text')}>
                  {formatDashboardMoney(paidThisMonth, i18n.language)}
                </dd>
              </div>
            )}
            {canAppointments && (
              <div>
                <dt className={cn(TYPE.small, 'text-ds-muted')}>
                  {t('dashboard.appointments_this_week', 'Aftaler denne uge')}
                </dt>
                <dd className={cn(TYPE.heading, TYPE_TABULAR, 'mt-s1 text-ds-text')}>
                  {countedPatientAppointments(weekApts.data ?? []).length}
                </dd>
              </div>
            )}
          </dl>
        )}
      </WidgetQueryState>
    </section>
  );
}

function OperationalAlertsWidget({ clinicId }: { clinicId: string | null }) {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canInvoices = Boolean(clinicId && hasPermission(clinicId, 'invoices', 'view'));
  const canSms = Boolean(clinicId && hasPermission(clinicId, 'sms', 'view'));

  const now = new Date();
  const invoicesQ = useInvoicesList(
    canInvoices ? clinicId : null,
    toInvoiceListQueryFilters(INVOICE_REPORT_FILTERS.outstanding, now),
  );
  const smsQ = useSmsClinic(canSms ? clinicId : null);

  const overdueInvoices = matchingInvoiceRows(
    invoicesQ.data ?? [],
    INVOICE_REPORT_FILTERS.overdue,
    now,
  );
  const partialInvoices = matchingInvoiceRows(
    invoicesQ.data ?? [],
    INVOICE_REPORT_FILTERS.partial,
    now,
  );
  const smsLowBalance =
    smsQ.data &&
    smsQ.data.credits.lowBalanceThreshold > 0 &&
    smsQ.data.credits.balance <= smsQ.data.credits.lowBalanceThreshold;

  const hasAny =
    (canInvoices && (overdueInvoices.length > 0 || partialInvoices.length > 0)) ||
    (canSms && smsLowBalance);
  const loading = (canInvoices && invoicesQ.isLoading) || (canSms && smsQ.isLoading);
  const error = (canInvoices && invoicesQ.isError) || (canSms && smsQ.isError);

  return (
    <section className="flex h-full min-h-0 flex-col" aria-labelledby="dashboard-reminders">
      <CardHeader
        className="flex-wrap"
        title={<span id="dashboard-reminders">{t('dashboard.alerts', 'Påmindelser')}</span>}
      />
      <WidgetQueryState
        loading={loading}
        error={error}
        onRetry={() => {
          if (canInvoices) void invoicesQ.refetch();
          if (canSms) void smsQ.refetch();
        }}
      >
        {hasAny ? (
          <ul className="mt-s3 space-y-s2">
            {canInvoices && overdueInvoices.length > 0 && (
              <li className={cn(TYPE.small, 'flex items-start gap-s3 text-ds-text')}>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ds-coral-tint text-ds-coral-ink">
                  <FileText className="h-4 w-4" aria-hidden />
                </span>
                <span>
                  {t('dashboard.x_overdue_invoices', '{{n}} forfaldne fakturaer venter på betaling.', {
                    n: overdueInvoices.length,
                  })}{' '}
                  <Link
                    to={destinationHref(invoiceDestination(INVOICE_REPORT_FILTERS.overdue))}
                    className="font-semibold text-ds-teal hover:text-ds-teal-700"
                  >
                    {t('dashboard.see_invoices', 'Se fakturaer')}
                  </Link>
                </span>
              </li>
            )}
            {canInvoices && partialInvoices.length > 0 && (
              <li className={cn(TYPE.small, 'flex items-start gap-s3 text-ds-text')}>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ds-amber-tint text-ds-amber-ink">
                  <FileText className="h-4 w-4" aria-hidden />
                </span>
                <span>
                  {t('dashboard.x_partial_invoices', '{{n}} fakturaer med delvis betaling', {
                    n: partialInvoices.length,
                  })}{' '}
                  <Link
                    to={destinationHref(invoiceDestination(INVOICE_REPORT_FILTERS.partial))}
                    className="font-semibold text-ds-teal hover:text-ds-teal-700"
                  >
                    {t('dashboard.see_invoices', 'Se fakturaer')}
                  </Link>
                </span>
              </li>
            )}
            {canSms && smsLowBalance && (
              <li className={cn(TYPE.small, 'flex items-start gap-s3 text-ds-text')}>
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ds-amber-tint text-ds-amber-ink">
                  <Wallet className="h-4 w-4" aria-hidden />
                </span>
                <Link to="/settings/sms" className="font-semibold text-ds-teal hover:text-ds-teal-700">
                  {t('dashboard.sms_low', 'SMS-saldo er lav — overvej at fylde op')}
                </Link>
              </li>
            )}
          </ul>
        ) : (
          <p className={cn(TYPE.small, 'mt-s3 text-ds-muted')}>
            {t('dashboard.no_alerts', 'Ingen påmindelser')}
          </p>
        )}
      </WidgetQueryState>
    </section>
  );
}

function MyTasksWidget({ clinicId }: { clinicId: string | null }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const query = useStaffTasks(clinicId);
  const mine = (query.data ?? [])
    .filter(
      (task) =>
        staffTaskIsOpen(task.status) &&
        Boolean(user?.id) &&
        task.assignees.some((assignee) => assignee.user_id === user?.id),
    )
    .sort((a, b) => compareStaffTasks(a, b))
    .slice(0, 5);

  return (
    <section className="flex h-full min-h-0 flex-col" aria-labelledby="dashboard-my-tasks">
      <CardHeader
        className="flex-wrap"
        title={<span id="dashboard-my-tasks">{t('dashboard.my_tasks')}</span>}
        count={mine.length > 0 ? mine.length : undefined}
        action={<WidgetNavLink to="/tasks">{t('dashboard.view_tasks')}</WidgetNavLink>}
      />
      <WidgetQueryState
        loading={query.isLoading}
        error={query.isError}
        onRetry={() => void query.refetch()}
      >
        {mine.length === 0 ? (
          <p className={cn(TYPE.small, 'mt-s3 text-ds-muted')}>{t('dashboard.my_tasks_empty')}</p>
        ) : (
          <ul className="mt-s2 min-h-0 flex-1 overflow-y-auto">
            {mine.map((task) => {
              const overdue = staffTaskIsOverdue(
                task.deadline_date,
                task.deadline_time,
                task.status,
              );
              return (
                <li key={task.id} className="border-b border-ds-line last:border-b-0">
                  <Link to="/tasks" className="flex items-start gap-s3 py-s3">
                    <div className="min-w-0 flex-1">
                      <p className={cn(TYPE.section, 'truncate text-ds-text')}>{task.title}</p>
                      <p className={cn(TYPE.small, overdue ? 'text-ds-danger' : 'text-ds-muted')}>
                        {overdue
                          ? t('tasks.deadline_filter.overdue')
                          : task.deadline_date
                            ? task.deadline_date
                            : t(`tasks.status.${task.status}`)}
                      </p>
                    </div>
                    <StatusChip tone={task.priority === 'high' ? 'coral' : 'neutral'}>
                      {t(`tasks.priority.${task.priority}`)}
                    </StatusChip>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </WidgetQueryState>
    </section>
  );
}

function ClinicalAlertsWidget({ clinicId }: { clinicId: string | null }) {
  const { t, i18n } = useTranslation();
  const { hasPermission } = useAuth();
  const clinicalAlertsQ = useClinicalAlerts(clinicId);
  const acknowledge = useAcknowledgeAlert(clinicId);
  const canAcknowledge = Boolean(
    clinicId && hasPermission(clinicId, 'questionnaires', 'update'),
  );
  const { map: templateNames } = useQuestionnaireNameMap(clinicId, i18n.language);
  const unnamed = t('dashboard.clinical_alert_unknown', 'Ukendt skema');
  const unackedAlerts = clinicalAlertsQ.data ?? [];

  return (
    <section className="flex h-full min-h-0 flex-col" aria-labelledby="dashboard-clinical-alerts">
      <CardHeader
        className="flex-wrap"
        title={<span id="dashboard-clinical-alerts">{t('dashboard.clinical_alerts', 'Kliniske advarsler')}</span>}
        count={unackedAlerts.length > 0 ? unackedAlerts.length : undefined}
      />
      <WidgetQueryState
        loading={clinicalAlertsQ.isLoading}
        error={clinicalAlertsQ.isError}
        onRetry={() => void clinicalAlertsQ.refetch()}
      >
        {unackedAlerts.length === 0 ? (
          <p className={cn(TYPE.small, 'mt-s3 flex items-center gap-s3 text-ds-muted')}>
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ds-green-tint text-ds-green-ink">
              <Check className="h-4 w-4" aria-hidden />
            </span>
            {t('dashboard.no_clinical_alerts', 'Ingen kliniske advarsler.')}
          </p>
        ) : (
          <ul className="mt-s3 space-y-s2">
            {unackedAlerts.slice(0, 5).map((alert) => (
              <li key={alert.id} className={cn(TYPE.small, 'flex items-start gap-s2 text-ds-text')}>
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-ds-coral-ink" aria-hidden />
                <Link
                  to={`/patients/${alert.patient_id}`}
                  className="min-w-0 flex-1 font-semibold text-ds-teal hover:text-ds-teal-700"
                >
                  {resolveQuestionnaireDisplayName({
                    templateId: alert.alert_data?.template_id ?? '',
                    snapshotName: alert.alert_data?.template_name,
                    locale: i18n.language,
                    templatesById: templateNames,
                    fallback: unnamed,
                  })}
                  {' — '}
                  {t('dashboard.clinical_alert_score', 'score {{score}}', {
                    score: alert.alert_data?.total_score ?? '?',
                  })}
                </Link>
                {canAcknowledge && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={acknowledge.isPending && acknowledge.variables === alert.id}
                    onClick={() => {
                      acknowledge.mutate(alert.id, {
                        onError: () =>
                          toast.error(
                            t(
                              'dashboard.clinical_alert_acknowledge_error',
                              'Kunne ikke kvittere advarslen',
                            ),
                          ),
                      });
                    }}
                  >
                    {t('dashboard.clinical_alert_acknowledge', 'Kvittér')}
                  </Button>
                )}
              </li>
            ))}
            {unackedAlerts.length > 5 && (
              <li className={cn(TYPE.small, 'text-ds-muted')}>
                {t('dashboard.clinical_alerts_more', '+ {{n}} flere', {
                  n: unackedAlerts.length - 5,
                })}
              </li>
            )}
          </ul>
        )}
      </WidgetQueryState>
    </section>
  );
}

function PatientMessagesWidget({ clinicId }: { clinicId: string | null }) {
  const { t, i18n } = useTranslation();
  const inbox = usePatientMessageInbox(clinicId);
  const emails = useAssignedPatientEmailInbox(clinicId);
  const markEmailRead = useMarkPatientEmailThreadRead(clinicId);
  const conversations = inbox.data ?? [];
  const emailRows = emails.data ?? [];
  const unreadCount = dashboardKommunikationUnreadCount(conversations, emailRows);
  const items = mergeDashboardKommunikationItems(conversations, emailRows);
  const now = new Date();
  const loading = inbox.isLoading || (emails.isLoading && emails.data === undefined);
  const isError = inbox.isError;

  return (
    <section className="flex h-full min-h-0 flex-col" aria-labelledby="dashboard-patient-messages">
      <CardHeader
        className="flex-wrap"
        title={
          <span id="dashboard-patient-messages" className="inline-flex items-center gap-s3">
            {t('dashboard.patient_messages', 'Kommunikation')}
            {unreadCount > 0 ? (
              <span
                className={cn(
                  TYPE.meta,
                  TYPE_TABULAR,
                  'inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-ds-coral-badge px-s2 text-ds-on-danger',
                )}
              >
                {unreadCount}
              </span>
            ) : null}
          </span>
        }
        action={
          <WidgetNavLink to="/patients/messages">
            {t('dashboard.view_messages', 'Se kommunikation')}
          </WidgetNavLink>
        }
      />

      <WidgetQueryState
        loading={loading}
        error={isError}
        onRetry={() => {
          void inbox.refetch();
          void emails.refetch();
        }}
      >
        {unreadCount === 0 ? (
          <p className={cn(TYPE.small, 'mt-s3 text-ds-muted')}>
            {t('dashboard.no_unread_messages', 'Ingen ulæst kommunikation')}
          </p>
        ) : (
          <ul className="mt-s2 min-h-0 flex-1 overflow-y-auto">
            {items.map((item) => {
              switch (item.kind) {
                case 'email': {
                  const patientName =
                    `${item.patientFirstName} ${item.patientLastName}`.trim() ||
                    t('dashboard.email_unknown_patient', 'Ukendt patient');
                  const sender = item.fromAddress?.trim() || t('email.no_subject', 'Uden emne');
                  const subject = item.subject?.trim() || t('email.no_subject', 'Uden emne');
                  return (
                    <li key={item.id} className="border-b border-ds-line last:border-b-0">
                      <Link
                        to={dashboardKommunikationEmailPath(item.patientId, item.threadId)}
                        className="flex items-start gap-s3 py-s3"
                        onClick={() => {
                          void markEmailRead.mutate(item.threadId);
                        }}
                      >
                        <span
                          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ds-teal-tint text-ds-teal"
                          aria-hidden
                        >
                          <Mail className="h-4 w-4" strokeWidth={1.75} />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline gap-s2">
                            <p className={cn(TYPE.section, 'min-w-0 flex-1 truncate text-ds-text')}>
                              {sender}
                            </p>
                            {item.sortAt ? (
                              <span className={cn(TYPE.small, TYPE_TABULAR, 'shrink-0 text-ds-muted')}>
                                {formatDashboardMessageTime(item.sortAt, now, i18n.language)}
                              </span>
                            ) : null}
                          </div>
                          <p className={cn(TYPE.small, 'mt-s1 line-clamp-2 text-ds-muted')}>
                            {subject}
                          </p>
                          <p className={cn(TYPE.small, 'mt-s1 truncate text-ds-muted')}>
                            {t('dashboard.email_channel', 'E-mail')}
                            {' · '}
                            {patientName}
                          </p>
                        </div>
                      </Link>
                    </li>
                  );
                }
                case 'message': {
                  const conversation = item.conversation;
                  const sender = conversationLatestSender(conversation);
                  const roleLabel = t(
                    messageSenderRoleKey(sender.role),
                    defaultMessageSenderRoleLabel(sender.role),
                  );
                  return (
                    <li key={item.id} className="border-b border-ds-line last:border-b-0">
                      <Link
                        to={patientMessagesPath(
                          conversation.patient_id,
                          conversation.latest_message_id,
                        )}
                        className="flex items-start gap-s3 py-s3"
                      >
                        <Avatar name={sender.name || patientDisplayName(conversation)} size={36} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline gap-s2">
                            <p className={cn(TYPE.section, 'min-w-0 flex-1 truncate text-ds-text')}>
                              {sender.name}
                            </p>
                            <span className={cn(TYPE.small, TYPE_TABULAR, 'shrink-0 text-ds-muted')}>
                              {formatDashboardMessageTime(
                                conversation.latest_created_at,
                                now,
                                i18n.language,
                              )}
                            </span>
                          </div>
                          <p className={cn(TYPE.small, 'mt-s1 line-clamp-2 text-ds-muted')}>
                            {conversation.latest_content.replace(/\s+/g, ' ').trim()}
                          </p>
                          {sender.role !== 'patient' && (
                            <p className={cn(TYPE.small, 'mt-s1 truncate text-ds-muted')}>
                              {roleLabel}
                              {' · '}
                              {patientDisplayName(conversation)}
                            </p>
                          )}
                        </div>
                      </Link>
                    </li>
                  );
                }
                default: {
                  const _exhaustive: never = item;
                  return _exhaustive;
                }
              }
            })}
          </ul>
        )}
      </WidgetQueryState>
    </section>
  );
}

function KpiCard({
  icon: Icon,
  label,
  value,
  hint,
  loading,
  error,
  onRetry,
  accent,
  to,
  cta,
}: {
  icon: LucideIcon;
  label: string;
  value: string;
  hint?: string;
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  accent?: 'warning';
  to: To;
  cta: string;
}) {
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className={cn(TYPE.small, 'flex items-center gap-s2 font-semibold text-ds-text')}>
        <span
          className={cn(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
            accent === 'warning'
              ? 'bg-ds-coral-tint text-ds-coral-ink'
              : 'bg-ds-teal-tint text-ds-teal',
          )}
        >
          <Icon className="h-4 w-4" strokeWidth={1.75} aria-hidden />
        </span>
        {label}
      </div>
      {loading ? (
        <SkeletonText lines={2} className="mt-s3" />
      ) : error ? (
        <WidgetQueryState loading={false} error onRetry={onRetry}>
          {null}
        </WidgetQueryState>
      ) : (
        <>
          <p className={cn(TYPE.title, TYPE_TABULAR, 'mt-s3 text-ds-text')}>{value}</p>
          {hint ? <p className={cn(TYPE.small, 'mt-s2 text-ds-muted')}>{hint}</p> : null}
        </>
      )}
      <div className="mt-auto pt-s4">
        <WidgetNavLink to={to}>{cta}</WidgetNavLink>
      </div>
    </div>
  );
}
